import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';

// ✅ 1. IMPORT THE DATEPICKER COMPONENT AND ITS CSS
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { FiUser, FiMail, FiMapPin, FiPhone, FiEdit, FiSave, FiXCircle, FiMap, FiNavigation, FiBriefcase, FiHome, FiBookOpen, FiCalendar, FiUsers } from 'react-icons/fi';
import './ProfilePage.css';

const ProfilePage = () => {
  const { user: contextUser, updateUser } = useAuth();

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', address: '', phone: '', location: '', locationId: '', locationType: '', dob: '', gender: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [isFetchingOffices, setIsFetchingOffices] = useState(false);

  useEffect(() => {
    if (contextUser) {
      setFormData({
        name: contextUser.name || '',
        email: contextUser.email || '',
        address: contextUser.address || '',
        phone: contextUser.phone || '',
        location: contextUser.location || '',
        locationId: contextUser.locationId || '',
        locationType: contextUser.locationType || 'individual',
        dob: contextUser.dob || '',
        gender: contextUser.gender || ''
      });
    }
  }, [contextUser]);

  useEffect(() => {
    const fetchOfficeLocations = async () => {
      setIsFetchingOffices(true);
      try {
        const locationsCol = collection(db, 'locations');
        const locationSnapshot = await getDocs(locationsCol);
        const locationsList = locationSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOfficeLocations(locationsList);
      } catch (error) {
        console.error('Error fetching office locations: ', error);
      } finally {
        setIsFetchingOffices(false);
      }
    };
    if (isEditMode && formData.locationType === 'office') { fetchOfficeLocations(); }
  }, [isEditMode, formData.locationType]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    const initials = names.map(n => n[0]).join('');
    return initials.toUpperCase().substring(0, 2);
  };

  const generateLocationId = () => `LOC_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`.toUpperCase();
  const handleInputChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  // ✅ 2. NEW HANDLER FOR THE DATEPICKER
  const handleDateChange = (date) => {
    // Format date as YYYY-MM-DD string to store in Firestore
    const formattedDate = date ? date.toISOString().split('T')[0] : '';
    setFormData(prev => ({ ...prev, dob: formattedDate }));
  };

  const handleOfficeSelect = (e) => {
    const officeId = e.target.value;
    if (!officeId) {
      setFormData(prev => ({ ...prev, address: '', location: '', locationId: '' }));
      return;
    }
    const office = officeLocations.find(o => o.id === officeId);
    if (office) {
      setFormData(prev => ({ ...prev, address: office.address || '', location: `${office.latitude}, ${office.longitude}`, locationId: office.locationId || '' }));
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        const { latitude, longitude } = pos.coords;
        setFormData(prev => ({ ...prev, location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`, locationId: generateLocationId() }));
        alert('Location fetched! Please save changes.');
      }, () => alert('Could not get location. Please grant permission.'));
    } else {
      alert('Geolocation is not supported.');
    }
  };

  const handleSaveChanges = async () => {
    if (!contextUser || !contextUser.docId) {
      alert('CRITICAL ERROR: User ID missing. Please re-login.');
      return;
    }
    setIsLoading(true);

    try {
      const userDocRef = doc(db, 'customer', contextUser.docId);
      const updatedData = { ...formData };

      await setDoc(userDocRef, updatedData, { merge: true });

      const newUserData = { ...contextUser, ...updatedData };
      updateUser(newUserData);

      alert('Profile updated successfully!');
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating profile: ', error);
      alert('Failed to update profile.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!contextUser) {
    return <div className="profile-loading">Loading Profile...</div>;
  }

  const getLocationTypeIcon = (type) => {
    switch(type) {
      case 'office': return <FiBriefcase className="detail-icon" />;
      case 'student': return <FiBookOpen className="detail-icon" />;
      default: return <FiHome className="detail-icon" />;
    }
  };

  return (
    <div className="profile-page-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar"><span>{getInitials(contextUser.name)}</span></div>
          <h2 className="profile-name">{contextUser.name || 'User Name'}</h2>
          <p className="profile-email">{contextUser.email || 'user@example.com'}</p>
          <p className="profile-customer-id">Customer ID: {contextUser.customerId}</p>
        </div>
        <div className="profile-body">
          <h3 className="profile-section-title">Personal Information</h3>
          {isEditMode ? (
            <>
              <div className="profile-detail-group editable"><FiUser className="detail-icon" /><input type="text" name="name" value={formData.name} onChange={handleInputChange} className="detail-value-input" /></div>
              <div className="profile-detail-group editable"><FiMail className="detail-icon" /><input type="email" name="email" value={formData.email} onChange={handleInputChange} className="detail-value-input" /></div>
              <div className="profile-detail-group editable"><FiPhone className="detail-icon" /><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="detail-value-input" /></div>
              
              {/* ✅ 3. REPLACE THE OLD INPUT WITH THE NEW DATEPICKER COMPONENT */}
              <div className="profile-detail-group editable">
                <FiCalendar className="detail-icon" />
                <DatePicker
                  selected={formData.dob ? new Date(formData.dob) : null}
                  onChange={handleDateChange}
                  className="detail-value-input"
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select Date of Birth"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  maxDate={new Date()} // Users cannot be born in the future
                />
              </div>

              <div className="profile-detail-group editable">
                <FiUsers className="detail-icon" />
                <select name="gender" value={formData.gender} onChange={handleInputChange} className="detail-value-input">
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>

              <div className="profile-detail-group editable">
                {getLocationTypeIcon(formData.locationType)}
                <select name="locationType" value={formData.locationType} onChange={handleInputChange} className="detail-value-input">
                  <option value="individual">Individual</option>
                  <option value="office">Office</option>
                  <option value="student">Student</option>
                </select>
              </div>
              
              {formData.locationType === 'individual' && (
                <>
                  <div className="profile-detail-group editable"><FiMapPin className="detail-icon" /><input type="text" name="address" placeholder="Enter full address" value={formData.address} onChange={handleInputChange} className="detail-value-input" /></div>
                  <div className="profile-detail-group editable location-group"><FiMap className="detail-icon" /><input type="text" name="location" value={formData.location} onChange={handleInputChange} placeholder="Coordinates (e.g., 11.02, 76.99)" className="detail-value-input" /><button onClick={handleGetLocation} className="location-btn">Get Current Location</button></div>
                </>
              )}
              {formData.locationType === 'office' && (
                <>
                  <div className="profile-detail-group editable"><FiMapPin className="detail-icon" /><select onChange={handleOfficeSelect} className="detail-value-input"><option value="">{isFetchingOffices ? 'Loading...' : 'Select an Office'}</option>{officeLocations.map(office => (<option key={office.id} value={office.id}>{office.companyName}</option>))}</select></div>
                  <div className="profile-detail-group editable"><FiMap className="detail-icon" /><input type="text" name="location" value={formData.location} placeholder="Office Coordinates" className="detail-value-input" readOnly /></div>
                </>
              )}

              <div className="profile-detail-group editable location-group"><FiNavigation className="detail-icon" /><input type="text" name="locationId" value={formData.locationId} placeholder="Location ID" className="detail-value-input" readOnly /></div>
            </>
          ) : (
            <>
              <div className="profile-detail-group"><FiCalendar className="detail-icon" /><span className="detail-value">{contextUser.dob || 'Not set'}</span></div>
              <div className="profile-detail-group"><FiUsers className="detail-icon" /><span className="detail-value">{contextUser.gender || 'Not set'}</span></div>
              <div className="profile-detail-group">
                {getLocationTypeIcon(contextUser.locationType)}
                <span className="detail-value">{contextUser.locationType ? contextUser.locationType.charAt(0).toUpperCase() + contextUser.locationType.slice(1) : 'Not set'}</span>
              </div>
              <div className="profile-detail-group"><FiPhone className="detail-icon" /><span className="detail-value">{contextUser.phone || 'Not set'}</span></div>
              <div className="profile-detail-group"><FiMapPin className="detail-icon" /><span className="detail-value">{contextUser.address || 'Not set'}</span></div>
              <div className="profile-detail-group"><FiMap className="detail-icon" /><span className="detail-value">{contextUser.location || 'Not set'}</span></div>
              <div className="profile-detail-group"><FiNavigation className="detail-icon" /><span className="detail-value">{contextUser.locationId || 'Not set'}</span></div>
            </>
          )}
        </div>
        <div className="profile-actions">
          {isEditMode ? (
            <>
              <button onClick={handleSaveChanges} disabled={isLoading} className="save-btn"><FiSave /> {isLoading ? 'Saving...' : 'Save Changes'}</button>
              <button onClick={() => setIsEditMode(false)} className="cancel-btn"><FiXCircle /> Cancel</button>
            </>
          ) : (
            <button onClick={() => setIsEditMode(true)} className="edit-btn"><FiEdit /> Edit Profile</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;